import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, Search, Library, UserCircle, LogIn, Settings as SettingsIcon, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

/* ──────────────────────────────────────────────────────────────────────
 * MobileNav — floating brutalist tab bar (1o1 theme).
 *
 * Visual language
 * ───────────────
 *   • Floats above the BottomPlayer as a separate glass pill, matching
 *     the player's width and corner radius so the two stack like layered
 *     cards rather than one tall slab.
 *   • Each tab uses a tiny numeric kicker ("01"…"05") and a Cabinet
 *     Grotesk uppercase label — editorial / brutalist signature of
 *     the 1o1 visual identity.
 *   • Active tab gets a sliding vermilion pill BEHIND the icon (Framer
 *     Motion `layoutId`) that physics-animates between routes, plus a
 *     2px vermilion top-line indicator and a vermilion-tinted icon.
 *   • Sharp inner radius (rounded-xl) inside a rounded-2xl shell — sharp
 *     accents inside soft shell, the inverse of the Apple Music tab bar.
 * ────────────────────────────────────────────────────────────────────── */

const Item = ({ to, num, icon: Icon, label, testId, onClick, badge, isActive }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={testId}
    onClick={onClick}
    className="relative flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 py-1.5 active:scale-[0.96] transition-transform"
  >
    {/* Sliding active pill — Framer Motion shares this element via
        layoutId across all tabs, so it physics-animates between
        positions whenever the route changes. */}
    {isActive && (
      <motion.div
        layoutId="mobile-nav-active-pill"
        className="absolute inset-x-1 inset-y-0.5 rounded-xl bg-gradient-to-b from-vermilion/25 via-vermilion/10 to-vermilion/5 border border-vermilion/40 shadow-[0_0_20px_-4px_rgba(232,69,37,0.55)]"
        transition={{ type: "spring", stiffness: 520, damping: 38 }}
      />
    )}
    {/* 2px vermilion top accent line — overlaps the shell border on
        active tab, giving a hard editorial mark above the soft pill. */}
    {isActive && (
      <motion.div
        layoutId="mobile-nav-active-line"
        className="absolute -top-[1px] left-3 right-3 h-[2px] bg-vermilion"
        transition={{ type: "spring", stiffness: 520, damping: 38 }}
      />
    )}

    <div className="relative z-10 flex flex-col items-center gap-0.5">
      {/* Tiny numbered kicker — 1o1's editorial signature. Only shown
          when active to avoid noise on the inactive tabs. */}
      <span
        className={`text-[8px] font-black tracking-[0.18em] leading-none transition-colors ${
          isActive ? "text-vermilion" : "text-transparent"
        }`}
      >
        {num}
      </span>
      <div className="relative">
        <Icon
          size={20}
          strokeWidth={isActive ? 2.4 : 2}
          className={`transition-colors ${isActive ? "text-vermilion drop-shadow-[0_0_6px_rgba(232,69,37,0.6)]" : "text-white/55"}`}
        />
        {badge ? (
          <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-vermilion text-white text-[9px] font-bold leading-[16px] text-center">
            {badge}
          </span>
        ) : null}
      </div>
      <span
        className={`font-display text-[9px] font-black uppercase tracking-[0.1em] leading-none transition-colors ${
          isActive ? "text-white" : "text-white/55"
        }`}
      >
        {label}
      </span>
    </div>
  </NavLink>
);

const MobileNav = () => {
  const { user, login } = useAuth();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [pendingClaims, setPendingClaims] = useState(0);

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      setPendingClaims(0);
      return;
    }
    api
      .get("/admin/whoami")
      .then((r) => setIsAdmin(!!r.data?.is_admin))
      .catch(() => setIsAdmin(false));
  }, [user]);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/uploads/admin/artist-claims", { params: { status: "pending" } })
      .then((r) => setPendingClaims(r.data?.counts?.pending || 0))
      .catch(() => setPendingClaims(0));
  }, [isAdmin]);

  // Profile tab routes:
  //   - signed in    → /profile (own page)
  //   - signed out   → trigger login flow
  const profileTo = user ? "/profile" : "/sign-in";
  const handleProfileClick = (e) => {
    if (user) return;
    e.preventDefault();
    login();
  };

  // Build the tab list dynamically so we can compute `isActive` based
  // on `location.pathname` (NavLink's own active state isn't accessible
  // outside its render prop). Order is numbered 01…05/06.
  const isPath = (p) => (p === "/" ? location.pathname === "/" : location.pathname.startsWith(p));

  const tabs = [
    { to: "/", num: "01", icon: Home, label: "Home", testId: "mobile-nav-home" },
    { to: "/search", num: "02", icon: Search, label: "Search", testId: "mobile-nav-search" },
    { to: "/library", num: "03", icon: Library, label: "Library", testId: "mobile-nav-library" },
    {
      to: profileTo,
      num: "04",
      icon: user ? UserCircle : LogIn,
      label: user ? "Profile" : "Sign in",
      testId: "mobile-nav-profile",
      onClick: handleProfileClick,
    },
  ];
  if (isAdmin) {
    tabs.push({
      to: "/admin",
      num: "05",
      icon: ShieldCheck,
      label: "Admin",
      testId: "mobile-nav-admin",
      badge: pendingClaims > 0 ? (pendingClaims > 9 ? "9+" : String(pendingClaims)) : null,
    });
  }
  tabs.push({
    to: "/settings",
    num: isAdmin ? "06" : "05",
    icon: SettingsIcon,
    label: "Settings",
    testId: "mobile-nav-settings",
  });

  return (
    <nav
      data-testid="mobile-nav"
      className="md:hidden fixed bottom-[100px] left-1/2 -translate-x-1/2 w-[96%] max-w-5xl z-40 bg-black/75 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-[0_18px_50px_-12px_rgba(0,0,0,0.75)] flex items-stretch px-1.5 pt-1 pb-[max(0.35rem,env(safe-area-inset-bottom))]"
    >
      {tabs.map((t) => (
        <Item key={t.to + t.label} {...t} isActive={isPath(t.to)} />
      ))}
    </nav>
  );
};

export default MobileNav;
